<template>
  <UForm ref="formRef" :state="form" :schema="schema">
    <UFormField
      class="mb-3 grid grid-cols-1 md:grid-cols-[150px_1fr] gap-0 md:gap-4 items-start"
      :label="$t('products.object_name')"
      name="idProduct"
    >
      <div class="mt-0 md:-mt-2.5">
        <USelectMenu
          value-key="value"
          :items="productOptions"
          v-model="form.idProduct"
          v-model:open="productListIsOpen"
          :loading="loadingProducts"
          @update:search-term="onProductSearch"
          @update:model-value="onProductChange"
          :placeholder="$t('common.form_control.select_select')"
          class="w-full"
          color="neutral"
          :search-input="{ placeholder: $t('common.form_control.uselectmenu_placeholder'), icon: 'i-lucide-search' }"
        />
      </div>
    </UFormField>

    <UFormField v-if="selectedProductHasChildren"
      class="mb-3 grid grid-cols-1 md:grid-cols-[150px_1fr] gap-0 md:gap-4 items-start"
      :label="$t('parts.object_name')"
      name="idProductChild"
    >
      <div class="mt-0 md:-mt-2.5">
        <div v-if="loadingChildrenProducts" class="py-1.5"><USkeleton class="h-4 w-[200px]"/></div>
        <USelectMenu v-else
          value-key="value"
          :items="productChildrenOptions"
          v-model="form.idProductChild"
          :placeholder="$t('common.form_control.select_select')"
          class="w-full"
          color="neutral"
          :search-input="{ placeholder: $t('common.form_control.uselectmenu_placeholder'), icon: 'i-lucide-search' }"
        />
      </div>
    </UFormField>

    <UFormField class="mb-3 grid grid-cols-1 md:grid-cols-[150px_1fr] gap-0 md:gap-4 items-start"
       :label="$t('common.object.quantity')" name="quantity">
      <div class="mt-0 md:-mt-2.5">
        <UInput v-model.number="form.quantity" class="w-full" color="neutral" type="number" min="0" step="any" />
      </div>
    </UFormField>

    <UFormField class="mb-3 grid grid-cols-1 md:grid-cols-[150px_1fr] gap-0 md:gap-4 items-start"
       :label="$t('non_conforming_products.non_conformity')" name="idNonConformity"
       :help="autoDispositionHint">
      <div class="mt-0 md:-mt-2.5">
        <div v-if="loadingNonConformities" class="py-1.5"><USkeleton class="h-4 w-[200px]"/></div>
        <USelectMenu v-else
          value-key="value"
          :items="nonConformityOptions"
          v-model="form.idNonConformity"
          :placeholder="$t('common.form_control.select_select')"
          class="w-full"
          color="neutral"
          :search-input="{ placeholder: $t('common.form_control.uselectmenu_placeholder'), icon: 'i-lucide-search' }"
        />
      </div>
    </UFormField>

    <UFormField class="mb-1 grid grid-cols-1 md:grid-cols-[150px_1fr] gap-0 md:gap-4 items-start"
       :label="$t('non_conforming_products.comments')" name="comments">
      <div class="mt-0 md:-mt-2.5">
        <UTextarea v-model="form.comments" class="w-full" color="neutral" :rows="3" autoresize :maxlength="1000" />
      </div>
    </UFormField>
  </UForm>
</template>

<script lang="ts" setup>
const { t: $t } = useI18n()

  import { ref, reactive, computed, onMounted } from 'vue'
  import { z } from 'zod'
  import { useDebounceFn } from '@vueuse/core'
  import type { Product } from '~/types/product'
  import type { NonConformingProduct, NonConformity } from '~/types/nonConformingProducts'

  type Option = { label: string; value: string; hasChildren?: boolean; defaultDispositionName?: string | null }

  const toast = useToast()
  const formRef = ref()
  const { t } = useI18n()

  const form = reactive({
    idProduct: undefined as string | undefined,
    idProductChild: undefined as string | undefined,
    quantity: undefined as number | undefined,
    idNonConformity: undefined as string | undefined,
    comments: '' as string,
  })

  const schema = z.object({
    idProduct: z.string({ required_error: t('common.message.form_validation.must_select_option') })
      .min(1, t('common.message.form_validation.must_select_option')),
    quantity: z.number({
        required_error: t('common.message.form_validation.required'),
        invalid_type_error: t('common.message.form_validation.value_must_be_numeric')
      })
      .positive(t('common.message.form_validation.value_must_be_greater_than_zero')),
    idNonConformity: z.string({ required_error: t('common.message.form_validation.must_select_option') })
      .min(1, t('common.message.form_validation.must_select_option')),
    comments: z.string().max(1000).optional(),
  })

  // ---------------- Producto (y parte, si el producto tiene hijos) ----------------
  const productListIsOpen = ref(false)
  const loadingProducts = ref(false)
  const productOptions = ref<Option[]>([])
  const selectedProductHasChildren = ref(false)
  const loadingChildrenProducts = ref(false)
  const productChildrenOptions = ref<Option[]>([])

  function productLabel(p: Product) {
    return `${p.partNumber ?? '(No Part Number)'} - ${p.name}`
  }

  async function loadProducts(search = '') {
    loadingProducts.value = true
    const response = await useApiFetchPage<Product>('/product/paginate', {
      params: { pageNumber: 1, pageSize: 20, parentProduct: 0, ...(search && { search }) }
    })
    const records = response?.data?.records ?? []
    const selected = productOptions.value.find(o => o.value === form.idProduct)
    productOptions.value = records.map(p => ({ label: productLabel(p), value: String(p.id), hasChildren: !!p.hasChildren }))
    // Conserva el producto seleccionado aunque ya no aparezca en los resultados de la busqueda.
    if (selected && !productOptions.value.some(o => o.value === selected.value))
      productOptions.value.unshift(selected)
    loadingProducts.value = false
  }

  const onProductSearch = useDebounceFn(async (term: string) => {
    if (!productListIsOpen.value) return
    await loadProducts(term)
  }, 350)

  async function onProductChange() {
    form.idProductChild = undefined
    const selected = productOptions.value.find(o => o.value === form.idProduct)
    selectedProductHasChildren.value = !!selected?.hasChildren
    if (!selectedProductHasChildren.value) return

    loadingChildrenProducts.value = true
    const { data } = await useApiFetchArray<Product>(`/product/getChildProducts?hasChildren=false&productId=${form.idProduct}`)
    productChildrenOptions.value = (data.value?.data ?? []).map((p: Product) => ({ label: productLabel(p), value: String(p.id) }))
    loadingChildrenProducts.value = false
  }

  // ---------------- No conformidades ----------------
  const { items: nonConformities, loading: loadingNonConformities, load: loadNonConformities } = useCatalogoPlano<NonConformity>('non-conformities')
  const nonConformityOptions = computed<Option[]>(() => nonConformities.value.map(nc => ({
    label: nc.name,
    value: String(nc.id),
    defaultDispositionName: nc.defaultDispositionName
  })))

  // Avisa que el registro quedara dispuesto automaticamente (p.ej. Contamination -> Scrap).
  const autoDispositionHint = computed(() => {
    const selected = nonConformityOptions.value.find(o => o.value === form.idNonConformity)
    return selected?.defaultDispositionName
      ? t('non_conforming_products.auto_disposition_hint', { disposition: selected.defaultDispositionName })
      : undefined
  })

  onMounted(async () => {
    await Promise.all([loadProducts(), loadNonConformities()])
  })

  async function submitForm(): Promise<{ success: boolean, message: string | null, data: NonConformingProduct | null }> {
    // Delinea los form controls de rojo si los campos están vacíos al presionar 'Guardar'.
    formRef.value?.submit()

    const result = schema.safeParse(form)
    const childMissing = selectedProductHasChildren.value && !form.idProductChild
    if (!result.success || childMissing) {
      const firstError = result.success
        ? t('common.message.form_validation.must_select_option')
        : Object.values(result.error.flatten().fieldErrors)[0]?.[0]
      toast.add({
        title: $t('common.message.form_validation.default_error_message'),
        description: firstError,
        color: 'error',
        icon: 'i-lucide-triangle-alert'
      })
      return { success: false, message: null, data: null }
    }

    // Si se eligio una parte, esa es el producto no conforme; si no, el producto seleccionado.
    const productId = Number(form.idProductChild || form.idProduct)

    try {
      swal_loading()
      const { data, error } = await useApiFetchObject<NonConformingProduct>('/nonConformingProducts/create', {
        method: 'POST',
        body: {
          productId,
          quantity: form.quantity,
          nonConformityId: Number(form.idNonConformity),
          comments: form.comments?.trim() || null,
        }
      })

      if (error.value || !data?.value?.success) {
        const message = data?.value?.message || $t('common.message.create.error')
        await swal_oops(message)
        return { success: false, message, data: null }
      }
      swal_close()
      return { success: true, message: data.value.message, data: data.value.data }
    } catch (err: any) {
      swal_close()
      toast.add({
        title: $t('common.message.form_validation.unexpected_error'),
        description: err.message,
        color: 'error',
        icon: 'i-lucide-triangle-alert'
      })
      return { success: false, message: err.message, data: null }
    }
  }

  defineExpose({
    submitForm,
  })
</script>
